import { memo, useCallback, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Textarea,
    makeStyles,
    Spinner,
} from '@fluentui/react-components';
import { SendRegular, WarningRegular, LockClosedRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useOtherAllocatedProjects, useGridProjectsWithAllocationDeficit, usePeriodData } from '../state';
import { useSubmitTimesheet } from '../hooks/useSubmitTimesheet';
import { useRetractTimesheet } from '../hooks/useRetractTimesheet';
import { useInitialData } from '../../../hooks/useInitialData';
import { AllocationProjectList } from './AllocationProjectList';
import { AllocationDeficitList } from './AllocationDeficitList';
import { TimesheetStatusBadge } from './TimesheetStatusBadge';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '8px',
    },
    commentField: {
        marginTop: '12px',
        width: '100%',
        '& textarea': {
            fieldSizing: 'content',
            maxHeight: '200px',
        },
    },
    allocationSection: {
        marginTop: '12px',
    },
});

type TimesheetStatusButtonProps = {
    onProjectClick?: (projectName: string) => void;
};

export const TimesheetStatusButton = memo(function TimesheetStatusButton({ onProjectClick }: TimesheetStatusButtonProps) {
    const styles = useStyles();
    const { t } = useTranslation('timesheet');
    const periodData = usePeriodData();
    const {
        data: {
            configuration: { allowTimeSheetComment, showAllocations },
        },
    } = useInitialData();
    const otherProjects = useOtherAllocatedProjects();
    const gridProjectDeficits = useGridProjectsWithAllocationDeficit();

    const { mutate: submitMutation, isPending: isSubmitPending } = useSubmitTimesheet();
    const { mutate: retractMutation, isPending: isRetractPending } = useRetractTimesheet();

    const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
    const [retractDialogOpen, setRetractDialogOpen] = useState(false);
    const [submitComment, setSubmitComment] = useState('');

    const timesheetState = periodData?.timesheetState;
    const periodState = periodData?.periodState;
    const userPeriodId = periodData?.id;
    const isClosed = periodState === 'Closed';

    const handleSubmitClick = useCallback(() => {
        setSubmitComment('');
        setSubmitDialogOpen(true);
    }, []);

    const handleRetractClick = useCallback(() => {
        setRetractDialogOpen(true);
    }, []);

    const handleSubmitConfirm = useCallback(() => {
        if (!userPeriodId) return;
        submitMutation(
            { userPeriodId, submitComment: submitComment || undefined },
            {
                onSettled: () => {
                    setSubmitDialogOpen(false);
                    setSubmitComment('');
                },
            },
        );
    }, [userPeriodId, submitComment, submitMutation]);

    const handleRetractConfirm = useCallback(() => {
        if (!userPeriodId) return;
        retractMutation(
            { userPeriodId },
            {
                onSettled: () => {
                    setRetractDialogOpen(false);
                },
            },
        );
    }, [userPeriodId, retractMutation]);

    const handleSubmitDialogClose = useCallback(() => {
        setSubmitDialogOpen(false);
        setSubmitComment('');
    }, []);

    const handleRetractDialogClose = useCallback(() => {
        setRetractDialogOpen(false);
    }, []);

    const handleAllocationProjectClick = useCallback(
        (projectName: string) => {
            setSubmitDialogOpen(false);
            onProjectClick?.(projectName);
        },
        [onProjectClick],
    );

    const canSubmit = timesheetState === 'Open' || timesheetState === 'Rejected';
    const canRetract = (timesheetState === 'Submitted' || timesheetState === 'Approved') && !isClosed;

    const submitIcon = useMemo(() => {
        if (!isClosed && canSubmit) {
            if (isSubmitPending) {
                return <Spinner size="tiny" />;
            }
            if (timesheetState === 'Rejected') {
                return <WarningRegular />;
            }
            return <SendRegular />;
        }
    }, [isClosed, canSubmit, isSubmitPending, timesheetState]);

    if (!periodData) return null;

    return (
        <div className={styles.container}>
            <TimesheetStatusBadge />

            {isClosed && (
                <Button appearance="subtle" size="medium" icon={<LockClosedRegular />} disabled>
                    {t('cta.action.closed')}
                </Button>
            )}

            {!isClosed && canSubmit && (
                <Button appearance="primary" size="medium" icon={submitIcon} onClick={handleSubmitClick} disabled={isSubmitPending}>
                    {t('cta.action.submit')}
                </Button>
            )}

            {canRetract && (
                <Button
                    appearance="outline"
                    size="medium"
                    icon={isRetractPending ? <Spinner size="tiny" /> : undefined}
                    onClick={handleRetractClick}
                    disabled={isRetractPending}
                >
                    {t('submit.dialog.confirm')}
                </Button>
            )}

            <Dialog open={submitDialogOpen} onOpenChange={(_e, data) => !data.open && handleSubmitDialogClose()}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>{t('submit.dialog.confirmation.title')}</DialogTitle>
                        <DialogContent>
                            {t('submit.dialog.confirmation.text')}
                            {showAllocations && gridProjectDeficits.length > 0 && (
                                <div className={styles.allocationSection}>
                                    <strong>{t('submit.dialog.missing.hours.title', 'Missing hours on allocated projects')}</strong>
                                    <AllocationDeficitList deficits={gridProjectDeficits} />
                                </div>
                            )}
                            {showAllocations && otherProjects.length > 0 && (
                                <div className={styles.allocationSection}>
                                    <strong>{t('submit.dialog.other.allocated.projects', 'Other allocated projects')}</strong>
                                    <AllocationProjectList onProjectClick={onProjectClick ? handleAllocationProjectClick : undefined} />
                                </div>
                            )}
                            {allowTimeSheetComment && (
                                <div>
                                    <Textarea
                                        className={styles.commentField}
                                        value={submitComment}
                                        onChange={(_e, data) => setSubmitComment(data.value)}
                                        resize="none"
                                        placeholder={t('approve.comment')}
                                    />
                                </div>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={handleSubmitDialogClose}>
                                {t('submit.dialog.confirmation.cancel')}
                            </Button>
                            <Button appearance="primary" onClick={handleSubmitConfirm} disabled={isSubmitPending} data-testid="submit-confirm">
                                {isSubmitPending ? <Spinner size="tiny" /> : t('submit.dialog.confirmation.submit')}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            <Dialog open={retractDialogOpen} onOpenChange={(_e, data) => !data.open && handleRetractDialogClose()}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>{t('submit.dialog.title')}</DialogTitle>
                        <DialogContent>
                            {timesheetState === 'Approved' ? t('submit.dialog.subtext.approved') : t('submit.dialog.subtext.default')}
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={handleRetractDialogClose}>
                                {t('submit.dialog.cancel')}
                            </Button>
                            <Button appearance="primary" onClick={handleRetractConfirm} disabled={isRetractPending} data-testid="retract-confirm">
                                {isRetractPending ? <Spinner size="tiny" /> : t('submit.dialog.confirm')}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
});
